import {
  ApplicationFlag,
  FamilyApplicationResp,
  FlagStatus,
} from '@/apis/family_application/requests'
import { usePaymentDetails } from '@/apis/payments/hooks/useGetPaymentDetail'
import { LogUserActivity } from '@/apis/users/requests.ts'
import { useApplicantProgramYears } from '@/app/SchoolYearProvider'
import PageLoader from '@/app/common/components/PageLoader/PageLoader'
import { LOG_TYPES } from '@/app/pages/family_application/constants.ts'
import Badge, { ColorType } from '@/common/components/Badge'
import { Button } from '@/common/components/Button'
import { Card, CardHeader } from '@/common/components/Card'
import { UserSquareIcon } from '@/common/icons/general'
import { cn } from '@/common/utilities'
import axiosInstance from '@/libs/axios_config'
import { User } from '@/queries/users/requests'
import { MessagesSchema } from '@/schemas/message.schema'
import { Payment } from '@/schemas/payments.schema'
import { RefreshFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { AppDispatch } from '@/storage/store'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import Conversation from './Conversation'
import PaymentStatusBadge from '@/app/common/components/student_payment_status_badge/payment_status_badge'

interface ChatProps {
  application: FamilyApplicationResp
  user: User
  payments: Payment[]
}
interface MessageItem {
  type: string
  id: number
  date: Date | null
  data: Payment | ApplicationFlag
}

export function Chat({ application, payments }: ChatProps) {
  const { selectedProgramId } = useApplicantProgramYears()
  const { t: translate } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLFormElement>(null)
  const [defaultHeight, setDefaultHeight] = useState(0)
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null)
  const dispatch = useDispatch<AppDispatch>()

  const queryClient = useQueryClient()

  const filteredFlags = application.flags.filter(
    (flag) => flag.hide_flag_on_messages == 0
  )
  const [selectedFlagId, setSelectedFlagId] = useState<number | null>(null)

  const flagQuery = useQuery({
    queryFn: async () => {
      if (!selectedFlagId) return []
      const response = await axiosInstance.get(
        `/scholar/flags/${selectedFlagId}/messages`
      )
      return MessagesSchema.parse(response.data.data)
    },
    queryKey: ['chat', selectedFlagId],
    enabled: !!selectedFlagId,
  })

  const paymentQuery = usePaymentDetails({
    payment_id: (selectedPaymentId ?? 0).toString(),
    program_id: selectedProgramId,
    messages: true
  })

  useEffect(() => {
    if (flagQuery.data || paymentQuery.data) {
      queryClient.invalidateQueries(['sessionUser'] as any)
    }
  }, [flagQuery.data, paymentQuery.data, queryClient])

  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Only update if the new height is greater than 0
        if (entry.contentRect.height > 0) {
          setDefaultHeight(entry.contentRect.height)
        }
      }
    })

    resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  function getFlagColor(flagStatus: FlagStatus): ColorType {
    const flagTable: Record<FlagStatus, ColorType> = {
      Resolved: 'Positive',
      'In Review': 'Neutral',
      Rejected: 'Negative',
    }

    return flagTable[flagStatus]
  }

  const selectedFlag = application.flags.find(
    (flag) => flag.id === selectedFlagId
  )

  const selectedPayment = payments?.find(
    (payment) => payment.payment_id === selectedPaymentId
  )

  const headerHeight = headerRef.current?.clientHeight || 0
  const textareaHeight = textareaRef.current?.clientHeight || 0

  const CHAT_AREA_HEIGHT = defaultHeight - headerHeight - textareaHeight - 24

  const onPaymentClick = (payment_id: number) => {
    setSelectedPaymentId(payment_id)
    dispatch(RefreshFamilyApplication(selectedProgramId))
    setSelectedFlagId(null)
    LogUserActivity({
      log_type: LOG_TYPES.VIEW_PAGE,
      suffix_text: 'Payment Message',
    })
  }

  function getFlagPersonName(flag: ApplicationFlag) {
    const name = flag?.student_full_name
      ? flag.student_full_name
      : flag?.applicant_full_name
        ? flag.applicant_full_name
        : ''
    return name ? ` (${name})` : ''
  }

  const onOpenMessage = (flag: ApplicationFlag) => {
    setSelectedPaymentId(null)
    dispatch(RefreshFamilyApplication(selectedProgramId))
    setSelectedFlagId(flag.id)
    LogUserActivity({
      log_type: LOG_TYPES.VIEW_PAGE,
      suffix_text:
        flag.flag_code == 'identity' ? 'Residency Message' : 'Messages page',
    })
  }

  const flagItems = filteredFlags.map((flag) => ({
    type: 'flag',
    id: flag.id,
    date: flag.last_message_datetime ? new Date(flag.last_message_datetime) : null,
    data: flag,
  }))

  const paymentItems = payments.map((payment) => {
    const latestMessage = payment.messages.reduce((latest, current) =>
      new Date(current.created_datetime) > new Date(latest.created_datetime) ? current : latest
    );
    return {
      type: 'payment',
      id: payment.payment_id,
      date: new Date(latestMessage.created_datetime),
      data: payment,
    };
  })

  console.log("payments", payments)

  const messageItems: MessageItem[] = [...flagItems, ...paymentItems].sort(
    (a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0
      const dateB = b.date ? new Date(b.date).getTime() : 0
      return dateB - dateA;
    }
  )

  if(!selectedPayment && !selectedFlag){
    if (messageItems.length > 0) {
      const firstItem = messageItems[0]
      if (firstItem.type === 'flag')
        setSelectedFlagId(firstItem.id)
       else if (firstItem.type === 'payment')
        setSelectedPaymentId(firstItem.id)
    }
  }

  return (

    <div
      ref={containerRef}
      className=" flex-1 w-full h-full mx-auto gap-4 flex flex-col lg:flex-row"
    >
      <Card className="lg:basis-5/12 h-fit">
        <CardHeader>
          <div className="flex items-center">
            <div className="h-10 w-10 bg-gray-100 border border-gray-200 rounded-full inline-flex items-center justify-center">
              <UserSquareIcon className="text-gray-600" />
            </div>
            <div className="flex flex-col ml-2">
              <div className="font-medium capitalize text-sm  mb-1 text-gray-800 leading-[100%]">
                {application.first_name} {application.last_name}
              </div>
            </div>
          </div>
        </CardHeader>
        {messageItems.length > 0 && (
          <div
          className="gap-2 p-4 overflow-y-auto"
          style={{ maxHeight: CHAT_AREA_HEIGHT }}
        >
          {messageItems.map((item) => {
            if (item.type === 'flag') {
              const flag = item.data as ApplicationFlag;
              return (
                <Button
                  key={`flag-${flag.id}`}
                  variant={'ghost'}
                  className={cn(
                    `flex border border-transparent shadow-none py-3 justify-between items-center text-left w-full rounded-0 text-sm`,
                    {
                      'bg-gray-50 border border-gray-200': selectedFlagId === flag.id,
                    }
                  )}
                  onClick={() => onOpenMessage(flag)}
                >
                  <div className="block font-medium text-gray-800">
                    {flag.flag_name}
                    <span className="font-bold">{getFlagPersonName(flag)}</span>
                  </div>
                  <div className="font-normal text-gray-700">
                    <Badge semanticColor={getFlagColor(flag.status)}>
                      {translate(flag.status)}
                    </Badge>
                  </div>
                </Button>
              );
            } else if (item.type === 'payment') {
              const payment = item.data as Payment;
              return (
                <Button
                  key={`payment-${payment.payment_id}`}
                  variant={'ghost'}
                  className={cn(
                    `flex border border-transparent shadow-none py-3 justify-between items-center text-left w-full rounded-0 text-sm`,
                    {
                      'bg-gray-50 border border-gray-200':
                        selectedPaymentId === payment.payment_id,
                    }
                  )}
                  onClick={() => onPaymentClick(payment.payment_id)}
                >
                  <div className="block font-medium text-gray-800">
                    {payment.transaction_type}
                    <span className="font-bold">
                      {' '}
                      ({payment.student_name ? payment.student_name.join(', ') : payment.applicant_name})
                    </span>
                  </div>
                  <div className="font-normal text-gray-700">
                    <PaymentStatusBadge paymentType={payment.type} paymentStatus={payment.status} />
                  </div>
                </Button>
              );
            }

            return null;
          })}
        </div>
        )}
      </Card>
      <Card className="flex-1 flex flex-col p-0 mx-0 px-0 ">
        <div ref={headerRef}>
          <CardHeader className="flex justify-between">
            {selectedFlag && (
              <>
                <div className="font-medium text-gray-800">
                  {selectedFlag?.flag_name}
                  <span className="font-bold">
                    {getFlagPersonName(selectedFlag)}
                  </span>
                </div>
                <Badge semanticColor={getFlagColor(selectedFlag?.status)}>
                  {translate(selectedFlag?.status)}
                </Badge>
              </>
            )}
            {selectedPayment && (
              <>
                <div className="font-medium text-gray-800">
                {selectedPayment.transaction_type}
                    <span className="font-bold">{' '}({selectedPayment.student_name ? selectedPayment.student_name.join(", ") : selectedPayment.applicant_name})</span>
                </div>
                <PaymentStatusBadge paymentType={selectedPayment.type} paymentStatus={selectedPayment.status} />
              </>
            )}
          </CardHeader>
        </div>
        {selectedPaymentId ?
          <PageLoader query={paymentQuery}
          >
            {(data) => {
              return (
                <Conversation messages={data.messages.filter((message)=>message.internal == 0)} chatAreaHeight={CHAT_AREA_HEIGHT} />
              )
            }}
          </PageLoader>
          : <PageLoader query={flagQuery}
          >
            {(messages) => {
              return (
                <Conversation messages={messages} chatAreaHeight={CHAT_AREA_HEIGHT} />
              )
            }}
          </PageLoader>
        }
      </Card>
    </div>
  )
}
